import {StateHandlerV2} from "../StateHandlerV2";
import {GenericActions} from "../../actions/GenericActions";
import {ArrayUtils, E_Modification, EO, get, StringUtils} from "@green24/npm-javascript-utils";
import {AppUtils} from "../../../class/AppUtils";

const defaultInitialState = {
	data: null,
	fetching: true,
	fetched: false,
	failed: false,
	error: null,
	lastUpdate: undefined,
};

export class ListStateHandler extends StateHandlerV2 {
	static get model() {
		return {
			defaultInitialState,
		}
	}

	constructor({actionPrefix, itemActionPrefix, initialState = defaultInitialState, listItemsPath = "content", itemUpdateDirtiesList = false}) {
		super({initialState, defaultInitialState});

		this.actionPrefix = actionPrefix;
		this.itemActionPrefix = itemActionPrefix;
		this.listItemsPath = listItemsPath;
		this.itemUpdateDirtiesList = itemUpdateDirtiesList;
	}

	setupActions() {
		const {actionPrefix, itemActionPrefix, initialState} = this;

		return {
			__INIT__: () => initialState,

			[`${actionPrefix}_${GenericActions.enum.actionType.FETCH_PENDING}`]: (state, action) => {
				return {
					...state,
					fetching: true,
					failed: false,
					error: null,
				};
			},
			[`${actionPrefix}_${GenericActions.enum.actionType.BACKGROUND_FETCH_PENDING}`]: (state, action) => {
				return {
					...state,
					fetching: true,
				};
			},
			[`${actionPrefix}_${GenericActions.enum.actionType.FETCH_ABORTED}`]: (state, action) => {
				return {
					...state,
					fetching: false,
				};
			},
			[`${actionPrefix}_${GenericActions.enum.actionType.FETCH_FULFILLED}`]: (state, action) => {
				return {
					...state,
					data: action.payload,
					fetched: true,
					fetching: false,
					failed: false,
					error: null,
					lastUpdate: Date.now(),
				};
			},
			[`${actionPrefix}_${GenericActions.enum.actionType.FETCH_REJECTED}`]: (state, action) => {
				return {
					...state,
					fetching: false,
					failed: true,
					error: action.payload,
				};
			},
			[`${actionPrefix}_${GenericActions.enum.actionType.CACHE_CLEAR}`]: (state, action) => {
				return {
					...state,
					lastUpdate: undefined,
				};
			},
			[`${itemActionPrefix}_${GenericActions.enum.actionType.EDIT_PENDING}`]: (state, action) => {
				return {
					...state,
					fetching: true,
				};
			},
			[`${itemActionPrefix}_${GenericActions.enum.actionType.EDIT_FULFILLED}`]: (state, action) => {
				return {
					...state,
					...this._handleGenericItemEdit(state, action.payload),
				};
			},
			[`${itemActionPrefix}_${GenericActions.enum.actionType.ITEM_DELETE}`]: (state, action) => {
				return {
					...state,
					...this._handleGenericItemDelete(state, action.payload),
				};
			},
			//Supporting alias in case there is already an old dependency or a mistake happens
			[`${actionPrefix}_${GenericActions.enum.actionType.ITEM_CLEAR}`]: (state, action) => {
				if(AppUtils.isDev) {
					console.warn(`This is a list reducer. Did you mean "${actionPrefix}_${GenericActions.enum.actionType.LIST_CLEAR}" instead of "${actionPrefix}_${GenericActions.enum.actionType.ITEM_CLEAR}"?`);
				}

				return {
					...initialState,
				};
			},
			[`${actionPrefix}_${GenericActions.enum.actionType.LIST_CLEAR}`]: (state, action) => {
				return {
					...initialState,
				};
			},
		}
	}

	_handleGenericItemEdit(state, item) {
		const rootPath = StringUtils.appendIfValid(this.listItemsPath, '.');
		const itemPath = `${rootPath}#${item.id}`;

		if(get(state.data, itemPath)) {
			if(this.itemUpdateDirtiesList) {
				return {
					lastUpdate: undefined,
				}
			}

			if(this.listItemsPath.length > 0) {
				let newEditData = EO(state.data).clone(true);
				newEditData.modifyV2(item, itemPath, E_Modification.ITEM_SET);

				return {
					fetching: false,
					data: newEditData,
					lastUpdate: Date.now(),
				}
			}

			return {
				fetching: false,
				data: state.data.map(dataItem => {
					if(dataItem.id == item.id) return item;
					return dataItem;
				}),
				lastUpdate: Date.now(),
			}
		}
	}

	_handleGenericItemDelete(state, item) {
		const collectionPath = this.listItemsPath;
		const INVALID = Symbol("INVALID");
		const itemsCollection = collectionPath ? get(state.data, collectionPath, INVALID) : state.data;

		if(AppUtils.isDev && state.data && itemsCollection === INVALID) {
			console.error(`value of the "listItemsPath" is not a valid path for the collection.\nstate.data:`, state.data, "\ncollectionPath:", '"' + collectionPath + '"');
		}

		if(this.itemUpdateDirtiesList) {
			if(itemsCollection) {
				if(ArrayUtils.findByID(itemsCollection, item.id)) {
					//Collection is valid and the item is present, invalidate it.
					return {
						lastUpdate: undefined,
					}
				}
				else {
					//Do nothing.
					//Collection is valid and the item is not present in the collection.
				}
			}
			else {
				//Collection is invalid for some reason. Just in case invalidate it all.
				return {
					lastUpdate: undefined,
				}
			}
		}

		if(itemsCollection) {
			if(collectionPath.length > 0) {
				let newData = EO(state.data).clone(true);
				newData.modifyV2(item.id ?? item, collectionPath, E_Modification.ARRAY_REMOVE_BY_ID);

				return {
					data: newData,
				}
			}

			return {
				data: itemsCollection.filter(dataItem => dataItem.id != (item.id ?? item)),
			}
		}
	}
}
